/**
 * Deduped transactional-page search orchestration, extracted from
 * `FilteredProductShowcase` so the guard + empty-results recovery are unit
 * testable in isolation.
 *
 * The `filters` watcher fires with `deep: true`, so it re-runs for every nested
 * mutation on mount (tags, filter-navigation wiring) even when the effective
 * Algolia query is unchanged. This function short-circuits those no-op re-runs
 * by comparing a serialized query signature (`searchKey`) against the last one
 * searched, and it drives the geo/empty-results recovery afterwards.
 */
import { classifyEmptySearchEngineRecovery } from '~/utils/searchEngineLocalityGeoFallback'

export type TransactionalSearchOutcome =
  | 'not_client'
  | 'skipped_unchanged'
  | 'error'
  | 'none'
  | 'reset_filters'
  | 'geo_retry'

export interface RunTransactionalSearchDeps {
  /** Only run on the client — SSR watcher triggers refetch in vain. */
  isClient: boolean
  /** Current serialized query signature. Read fresh (it changes with the geo flag). */
  getSearchKey: () => string
  /** Signature of the last query we actually searched (null once cleared). */
  getLastSearchKey: () => string | null
  /** Record the signature we are about to search — called synchronously before awaiting. */
  commitSearchKey: (key: string) => void
  /** Forget the last signature so a later trigger can retry (used on failure). */
  clearSearchKey: () => void
  keepNoResultsNoticeOnNextFilterSearch: boolean
  setKeepNoResultsNotice: (value: boolean) => void
  setShowNoResultsNotice: (value: boolean) => void
  setGeoFallback: (value: boolean) => void
  updateAmountOfHitsPerPagination: () => void
  updateFiltersStorage: () => void
  performSearch: () => Promise<unknown>
  /** Read after `performSearch` resolves. */
  getHits: () => unknown[] | undefined
  /** True when the last `performSearch` errored. */
  hasError: () => boolean
  getFiltersMatchDefault: () => boolean
  resetFiltersToDefault: () => void
}

export const runTransactionalSearchIfNeeded = async (
  deps: RunTransactionalSearchDeps
): Promise<TransactionalSearchOutcome> => {
  if (!deps.isClient) return 'not_client'

  // Skip when the effective query is unchanged (e.g. a nested filter mutation
  // that doesn't alter the Algolia request).
  if (deps.getSearchKey() === deps.getLastSearchKey())
    return 'skipped_unchanged'

  if (deps.keepNoResultsNoticeOnNextFilterSearch) {
    deps.setKeepNoResultsNotice(false)
  } else {
    deps.setShowNoResultsNotice(false)
  }

  deps.setGeoFallback(false)
  deps.updateAmountOfHitsPerPagination()
  deps.updateFiltersStorage()
  // Commit the key before awaiting so a concurrent run for the same query is
  // deduped mid-flight; cleared again below if the search fails.
  deps.commitSearchKey(deps.getSearchKey())
  await deps.performSearch()

  // A network error must not be treated as "no hits": drop the dedupe key so a
  // later trigger can retry, and skip the empty-results recovery.
  if (deps.hasError()) {
    deps.clearSearchKey()
    return 'error'
  }

  const recovery = classifyEmptySearchEngineRecovery({
    hits: deps.getHits(),
    filtersMatchDefault: deps.getFiltersMatchDefault(),
  })

  if (recovery === 'none') return 'none'

  deps.setShowNoResultsNotice(true)

  if (recovery === 'reset_filters') {
    deps.setKeepNoResultsNotice(true)
    deps.resetFiltersToDefault()
    return 'reset_filters'
  }

  deps.setGeoFallback(true)
  deps.commitSearchKey(deps.getSearchKey())
  await deps.performSearch()

  if (deps.hasError()) deps.clearSearchKey()
  return 'geo_retry'
}
